import { Injectable, signal } from '@angular/core';
import { environment } from '../environments/environment';

export interface AuthUser {
  name: string;
  avatarUrl: string;
}

interface SessionResponse {
  authenticated: boolean;
  user?: { login: string; name: string | null; email: string | null };
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly isAuthenticated = signal(false);
  readonly isLoading = signal(false);
  readonly currentUser = signal<AuthUser | null>(null);
  readonly error = signal<string | null>(null);

  readonly ready: Promise<void>;

  constructor() {
    this.ready = this.checkSession();
  }

  /** Re-runs the session check (e.g. after a bfcache restore, see login.ts). */
  async refreshSession(): Promise<void> {
    await this.checkSession();
  }

  private async checkSession(): Promise<void> {
    this.isLoading.set(true);
    try {
      const response = await fetch(`${environment.apiBaseUrl}/api/auth/github/session`, {
        credentials: 'include'
      });

      if (!response.ok) {
        this.isAuthenticated.set(false);
        this.currentUser.set(null);
        return;
      }

      const session: SessionResponse = await response.json();
      if (session.authenticated && session.user) {
        this.currentUser.set({ name: session.user.name ?? session.user.login, avatarUrl: '' });
        this.isAuthenticated.set(true);
      } else {
        this.isAuthenticated.set(false);
        this.currentUser.set(null);
      }
    } catch {
      // Backend unreachable or not started yet: treat as signed out, no error banner
      // needed for a silent background check on app start.
      this.isAuthenticated.set(false);
      this.currentUser.set(null);
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * @param returnUrl Local path to send the user back to after a successful login
   * (e.g. the page a route guard redirected them away from). Defaults to `/catalog`.
   */
  login(returnUrl = '/catalog'): void {
    this.error.set(null);

    const loginUrl = new URL(`${environment.apiBaseUrl}/api/auth/github/login`, window.location.origin);
    loginUrl.searchParams.set('returnUrl', returnUrl);
    window.location.href = loginUrl.toString();
  }

  async logout(): Promise<void> {
    try {
      await fetch(`${environment.apiBaseUrl}/api/auth/github/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch {
      // no-op
    } finally {
      this.isAuthenticated.set(false);
      this.currentUser.set(null);
      this.error.set(null);
    }
  }
}
